<template>
  <div class="tf-backdrop" @mousedown.self="$emit('close')">
    <div class="tf-modal">
      <div class="tf-header">
        <div>
          <h2 class="tf-title">{{ isEdit ? 'Edit template' : 'Create new template' }}</h2>
          <p class="tf-subtitle">{{ isEdit ? "Edit the template's details" : 'Please fill in the fields below' }}</p>
        </div>
        <button class="tf-close" @click="$emit('close')">
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div class="tf-body">
        <div class="tf-grid">
          <div class="tf-field tf-field--wide">
            <label class="tf-label">Name <span class="tf-req">*</span></label>
            <input
              v-model="form.name"
              class="tf-input"
              :class="{ 'tf-input--error': errors.name }"
              placeholder="pl. Sales order - alap"
              @input="errors.name = null"
            />
            <span v-if="errors.name" class="tf-error-msg">{{ errors.name }}</span>
          </div>

          <div class="tf-field">
            <label class="tf-label">Code</label>
            <input
              v-model="form.code"
              class="tf-input"
              :class="{ 'tf-input--error': errors.code }"
              placeholder="pl. sales_order_default"
              @input="errors.code = null"
            />
            <span v-if="errors.code" class="tf-error-msg">{{ errors.code }}</span>
          </div>

          <div class="tf-field tf-field--wide">
            <label class="tf-label">Context <span class="tf-req">*</span></label>
            <input
              v-model="form.context"
              class="tf-input"
              :class="{ 'tf-input--error': errors.context }"
              placeholder="pl. sales_order.customer_comment"
              @input="errors.context = null"
            />
            <span v-if="errors.context" class="tf-error-msg">{{ errors.context }}</span>
          </div>

          <div class="tf-field">
            <label class="tf-label">Type <span class="tf-req">*</span></label>
            <select v-model="form.type" class="tf-input">
              <option value="text">Text (text)</option>
              <option value="html">HTML</option>
              <option value="markdown">Markdown</option>
            </select>
          </div>

          <div class="tf-field">
            <label class="tf-label">Order</label>
            <input
              v-model.number="form.sort_order"
              class="tf-input"
              type="number"
              min="1"
            />
          </div>

          <div class="tf-field tf-field--full">
            <label class="tf-label">
              Content <span class="tf-req">*</span>
              <span class="tf-hint">Variables: {customer_name}, {order_number}, {delivery_date}</span>
            </label>
            <textarea
              v-model="form.content"
              class="tf-input tf-textarea"
              :class="{ 'tf-input--error': errors.content }"
              placeholder="Tisztelt {customer_name}&#10;&#10;A rendelés száma: {order_number}&#10;Várható szállítás dátuma: {delivery_date}."
              @input="errors.content = null"
            ></textarea>
            <span v-if="errors.content" class="tf-error-msg">{{ errors.content }}</span>
          </div>

          <div class="tf-field tf-field--full">
            <button
              class="tf-toggle-row"
              type="button"
              @click="form.is_active = !form.is_active"
            >
              <span
                class="tf-toggle"
                :class="{ 'tf-toggle--on': form.is_active }"
              >
                <span class="tf-toggle-thumb"></span>
              </span>
              <span class="tf-toggle-label">Active template</span>
              <span class="tf-toggle-desc">{{ form.is_active ? 'It will be visible in the selector' : 'Does not appear in the drop-down menu' }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="tf-footer">
        <button class="tf-btn-ghost" :disabled="loading" @click="$emit('close')">
          Cancel
        </button>
        <button class="tf-btn-primary" :disabled="loading" @click="submit">
          <span v-if="loading" class="tf-btn-spinner"></span>
          {{ isEdit ? 'Save' : 'Create' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const emptyForm = () => ({
  id: null,
  name: '',
  code: '',
  context: '',
  type: 'text',
  content: '',
  is_active: true,
  sort_order: 1,
})

export default {
  name: 'TemplateFormDialog',

  props: {
    template: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },

  data() {
    return {
      form: this.template ? { ...this.template } : emptyForm(),
      errors: {},
    }
  },

  computed: {
    isEdit() {
      return !!this.template?.id
    },
  },

  methods: {
    validate() {
      const e = {}
      if (!this.form.name?.trim()) e.name = 'You must enter a name'
      //if (!this.form.code?.trim()) e.code = 'You must enter a code'
      if (!this.form.context?.trim()) e.context = 'Providing context is required'
      if (!this.form.content?.trim()) e.content = 'Providing content is required'
      this.errors = e
      return Object.keys(e).length === 0
    },

    submit() {
      if (!this.validate()) return
      this.form.code = this.form.context + '_' + String(this.form.name.trim().toLocaleLowerCase()).split(' ').join('_');
      this.$emit('save', { ...this.form })
    },
  },
}
</script>

<style scoped>
.tf-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.tf-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.2);
}

.tf-header {
  padding: 22px 24px 18px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
}

.tf-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 3px;
}

.tf-subtitle {
  font-size: 12.5px;
  color: #94a3b8;
  margin: 0;
}

.tf-close {
  width: 30px;
  height: 30px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.15s;
  flex-shrink: 0;
}
.tf-close:hover { background: #f1f5f9; color: #1e293b; }

.tf-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.tf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.tf-field { display: flex; flex-direction: column; gap: 5px; }
.tf-field--wide { grid-column: 1 / 2; }
.tf-field--full { grid-column: 1 / -1; }

.tf-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tf-req { color: #ef4444; font-size: 13px; }

.tf-hint {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.tf-input {
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13.5px;
  color: #0f172a;
  background: #fff;
  outline: none;
  width: 100%;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.tf-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
}
.tf-input--error { border-color: #ef4444; }
.tf-input--error:focus { box-shadow: 0 0 0 3px rgba(239,68,68,0.1); }

.tf-textarea {
  resize: vertical;
  min-height: 120px;
  line-height: 1.6;
}

.tf-error-msg {
  font-size: 12px;
  color: #ef4444;
}

.tf-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: border-color 0.15s;
}
.tf-toggle-row:hover { border-color: #cbd5e1; }

.tf-toggle {
  position: relative;
  width: 36px;
  height: 20px;
  border-radius: 20px;
  background: #e2e8f0;
  flex-shrink: 0;
  transition: background 0.2s;
}
.tf-toggle--on { background: #2563eb; }

.tf-toggle-thumb {
  position: absolute;
  width: 14px;
  height: 14px;
  background: #fff;
  border-radius: 50%;
  top: 3px;
  left: 3px;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.tf-toggle--on .tf-toggle-thumb { transform: translateX(16px); }

.tf-toggle-label {
  font-size: 13.5px;
  font-weight: 500;
  color: #1e293b;
}

.tf-toggle-desc {
  font-size: 12px;
  color: #94a3b8;
  margin-left: auto;
}

.tf-footer {
  padding: 16px 24px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.tf-btn-ghost {
  padding: 9px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s;
}
.tf-btn-ghost:hover { background: #f8fafc; color: #1e293b; }
.tf-btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

.tf-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 20px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.tf-btn-primary:hover { background: #1d4ed8; }
.tf-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.tf-btn-spinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: tf-spin 0.7s linear infinite;
}
@keyframes tf-spin { to { transform: rotate(360deg); } }
</style>